import React, { useState, useEffect, useRef } from "react";
import TopBar from "../components/TopBar";
import ConfirmDeleteModal from "../components/common/ConfirmDeleteModal";
import LoadingSpinner from "../components/common/LoadingSpinner";
import UserEditModal from "../components/UserEditModal";
import { Home } from "lucide-react";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../services/users";
import "../components/UserList.css";
import { Toaster, toast } from "sonner";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "" });
  const [editUser, setEditUser] = useState(null);
  const [error, setError] = useState("");
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const editFormRef = useRef(null);

  const fetchUsers = async () => {
    setLoading(true);
    setApiError("");
    try {
      const data = await getUsers();
      const normalized = Array.isArray(data)
        ? data.map((u) => ({ ...u, _id: u._id || u.id }))
        : [];
      setUsers(normalized);
    } catch (err) {
      const errorMsg =
        err?.error && Array.isArray(err.error) && err.error.length
          ? err.error.join("\n")
          : err.message || "Failed to fetch users";
      setApiError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (
      !addForm.name.trim() ||
      !addForm.email.trim() ||
      !addForm.password.trim()
    ) {
      setError("Name, email, and password are required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await createUser({
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
      });
      setAddForm({ name: "", email: "", password: "" });
      setShowAddForm(false);
      await fetchUsers();
      toast.success("User added successfully");
    } catch (err) {
      const errorMsg =
        err?.error && Array.isArray(err.error) && err.error.length
          ? err.error.join("\n")
          : err.message || "Failed to add user";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditUser({ ...user, _id: user._id || user.id });
    setShowAddForm(false);
    setError("");
  };

  const handleDeleteUser = (id) => {
    setDeleteUserId(id);
  };

  const confirmDeleteUser = async () => {
    try {
      setLoading(true);
      await deleteUser(deleteUserId);
      setUsers(users.filter((u) => u._id !== deleteUserId));
      setDeleteUserId(null);
      toast.success("User deleted successfully");
    } catch (err) {
      const errorMsg =
        err?.error && Array.isArray(err.error) && err.error.length
          ? err.error.join("\n")
          : err.message || "Failed to delete user";
      setApiError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteUserId(null);
  };

  const handleEditModalClose = () => {
    setEditUser(null);
  };

  const handleEditModalSubmit = async (formData) => {
    try {
      setLoading(true);
      setError("");
      const updateData = { name: formData.name };
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password;
      }
      await updateUser(editUser._id || editUser.id, updateData);
      setEditUser(null);
      await fetchUsers();
      toast.success("User updated successfully");
    } catch (err) {
      const errorMsg =
        err?.error && Array.isArray(err.error) && err.error.length
          ? err.error.join("\n")
          : err.message || "Failed to update user";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <TopBar />
      <div
        className="users-page-container"
        style={{ minHeight: "100vh", background: "#f8fafc", padding: 0 }}
      >
        <div
          className="users-header"
          style={{
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            padding: "32px 0 16px 0",
            marginBottom: 24,
            textAlign: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 24,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
            onClick={() => (window.location.href = "/")}
            title="Go to Home"
          >
            <div
              style={{
                padding: 12,
                borderRadius: "50%",
                background: "var(--brand-primary)",
                boxShadow: "0 4px 15px rgba(0, 115, 230, 0.3)",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.1)";
                e.target.style.boxShadow =
                  "0 6px 20px rgba(102, 126, 234, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow =
                  "0 4px 15px rgba(102, 126, 234, 0.3)";
              }}
            >
              <Home size={28} color="#ffffff" />
            </div>
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: "var(--brand-primary)",
              margin: 0,
            }}
          >
            User Management
          </h1>
          <p style={{ color: "#64748b", fontSize: 18, margin: 0 }}>
            Add, edit, and remove users in your organization
          </p>
        </div>
        <div
          className="users-content"
          style={{ width: "100%", maxWidth: "100%", margin: 0, padding: 24 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 24,
            }}
          >
            <button
              className="add-user-btn"
              style={{
                background: "var(--brand-primary)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "12px 28px",
                fontWeight: 600,
                fontSize: 17,
                boxShadow: "0 2px 8px rgba(37,99,235,0.08)",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onClick={() => {
                setAddForm({ name: "", email: "", password: "" });
                setShowAddForm(true);
                setEditUser(null);
                setError("");
              }}
            >
              ＋ Add User
            </button>
          </div>
          {showAddForm && (
            <form
              className="add-user-form"
              onSubmit={handleAddUser}
              autoComplete="off"
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                padding: 32,
                marginBottom: 32,
                display: "flex",
                gap: 18,
                alignItems: "center",
                flexWrap: "wrap",
                width: "100%",
                maxWidth: 700,
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <input
                type="text"
                placeholder="Name"
                value={addForm.name}
                onChange={(e) =>
                  setAddForm({ ...addForm, name: e.target.value })
                }
                required
                autoComplete="new-username"
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: 12,
                  borderRadius: 8,
                  border: "1.2px solid #c7d2fe",
                  fontSize: 16,
                }}
              />
              <input
                type="email"
                placeholder="Email"
                value={addForm.email}
                onChange={(e) =>
                  setAddForm({ ...addForm, email: e.target.value })
                }
                required
                autoComplete="new-email"
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: 12,
                  borderRadius: 8,
                  border: "1.2px solid #c7d2fe",
                  fontSize: 16,
                }}
              />
              <input
                type="password"
                placeholder="Password"
                value={addForm.password}
                onChange={(e) =>
                  setAddForm({ ...addForm, password: e.target.value })
                }
                required
                autoComplete="new-password"
                style={{
                  flex: 1,
                  minWidth: 180,
                  padding: 12,
                  borderRadius: 8,
                  border: "1.2px solid #c7d2fe",
                  fontSize: 16,
                }}
              />
              <button
                type="submit"
                style={{
                  background: "var(--brand-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 28px",
                  fontWeight: 600,
                  fontSize: 17,
                  cursor: "pointer",
                }}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                style={{
                  background: "#f3f4f6",
                  color: "var(--brand-secondary)",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 28px",
                  fontWeight: 600,
                  fontSize: 17,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              {error && (
                <div
                  className="error-message"
                  style={{ color: "#dc2626", fontWeight: 500, marginLeft: 18 }}
                >
                  {error}
                </div>
              )}
            </form>
          )}
          <UserEditModal
            open={!!editUser}
            name={editUser?.name || ""}
            email={editUser?.email || ""}
            onClose={handleEditModalClose}
            onSubmit={handleEditModalSubmit}
          />
          {loading ? (
            <LoadingSpinner text="Loading users..." />
          ) : apiError ? (
            <div style={{ color: "#dc2626", fontWeight: 500, padding: 24 }}>
              {apiError}
            </div>
          ) : (
            <div
              className="users-table-container"
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                padding: 0,
                overflow: "auto",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <table
                className="users-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 17,
                }}
              >
                <thead style={{ background: "#f1f5f9" }}>
                  <tr>
                    <th
                      style={{
                        padding: 16,
                        fontSize: 16,
                        color: "#2563eb",
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: 16,
                        fontSize: 16,
                        color: "#2563eb",
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        padding: 16,
                        fontSize: 16,
                        color: "#2563eb",
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      Role
                    </th>
                    <th
                      style={{
                        padding: 16,
                        fontSize: 16,
                        color: "#2563eb",
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td style={{ padding: 16, fontWeight: 600 }}>
                        {user.name}
                      </td>
                      <td style={{ padding: 16 }}>{user.email}</td>
                      <td style={{ padding: 16 }}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </td>
                      <td style={{ padding: 16 }}>
                        {user.role !== "admin" ? (
                          <>
                            <button
                              className="edit-btn"
                              style={{
                                background: "#fbbf24",
                                color: "#fff",
                                border: "none",
                                borderRadius: 8,
                                padding: "8px 18px",
                                fontWeight: 600,
                                cursor: "pointer",
                                marginRight: 8,
                              }}
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </button>
                            <button
                              className="delete-btn"
                              style={{
                                background: "#dc2626",
                                color: "#fff",
                                border: "none",
                                borderRadius: 8,
                                padding: "8px 18px",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                              onClick={() => handleDeleteUser(user._id)}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <span style={{ color: "#64748b", fontWeight: 500 }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <ConfirmDeleteModal
        open={!!deleteUserId}
        name={users.find((u) => u._id === deleteUserId)?.name || ""}
        type="user"
        onCancel={cancelDeleteUser}
        onConfirm={confirmDeleteUser}
      />
    </>
  );
};

export default UsersPage;
